import { useQuery } from '@apollo/client';
import type {
  UseSegmentsListFetch,
  UseSegmentsListFetchProps,
} from '@openx/components-segment/lib/utils/context/SegmentHookTypes';
import type { FetchMore } from '@openx/components-segment/types';
import type { QueryVar } from '@openx/types/serverSideCriteria';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  GetSegmentFeedDocument,
  type GetSegmentFeedQuery,
  type GetSegmentFeedQueryVariables,
} from '../../../graphql/query/segments/GetSegmentFeed.generated';
import { GetSegmentsListDocument } from '../../../graphql/query/segments/GetSegmentsList.generated';

type QueryParams = GetSegmentFeedQueryVariables & QueryVar;

const EMPTY_SEGMENT_FEED: GetSegmentFeedQuery['segmentFeed'] = [];

export const useSegmentsInfinityScrollListFetch: UseSegmentsListFetch = ({
  variables,
  options,
  usePrefetch,
  skip = false,
}: UseSegmentsListFetchProps<QueryParams>) => {
  const [allSegments, setAllSegments] = useState<GetSegmentFeedQuery['segmentFeed']>([]);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const {
    client,
    data,
    error,
    loading,
    fetchMore: apolloFetchMore,
  } = useQuery(GetSegmentsListDocument, {
    onCompleted: ({ segment_aggregate }) => {
      options?.onCompleted?.(segment_aggregate.aggregate?.count);
      setTotalCount(segment_aggregate.aggregate?.count ?? 0);
    },
    variables,
    skip,
  });

  const { segmentFeed = EMPTY_SEGMENT_FEED } = data ?? {};

  useEffect(() => {
    setAllSegments(segmentFeed);
    setHasMore(segmentFeed.length < totalCount);
  }, [segmentFeed, totalCount]);

  // Ensure provider is always a defined Pick<Provider, "id" | "name">
  // segments should be required to have a provider on the api
  const segments = useMemo(
    () =>
      allSegments.map(segment => ({
        ...segment,
        provider: segment.provider ? { id: segment.provider.id, name: segment.provider.name } : { id: '', name: '' },
      })),
    [allSegments]
  );

  const loadMore = useCallback(async () => {
    const { data } = await apolloFetchMore({
      variables: { limit: variables.limit, offset: allSegments.length },
    });

    const newSegments = data?.segmentFeed ?? [];
    const totalCount = data?.segment_aggregate?.aggregate?.count ?? 0;

    if (newSegments.length === 0) {
      setHasMore(false);
      return;
    }

    setAllSegments(prev => [...prev, ...newSegments]);
    setHasMore(allSegments.length + newSegments.length < totalCount);
  }, [apolloFetchMore, allSegments.length, variables.limit]);

  const fetchMore: FetchMore<QueryParams> = useCallback(
    queryOptions => {
      client.query<GetSegmentFeedQuery, GetSegmentFeedQueryVariables>({
        query: GetSegmentFeedDocument,
        variables: { ...variables, ...queryOptions.variables },
      });
    },
    [variables, client]
  );

  usePrefetch?.(fetchMore);

  return {
    error,
    loading,
    segments,
    loadMore,
    hasMore,
    totalCount: data?.segment_aggregate?.aggregate?.count ?? 0,
  };
};
